// MetricRegistryContext — single source of truth for metric display
// metadata (label, unit, aggregation, palette, category) sourced from the
// backend metric registry (GET /api/data/registry/metrics + /aliases,
// services/dynamic-schema, branch feat/metric-registry-api / PR #21). See
// IMPORT_SPEC.md §2.1-2.3.
//
// -----------------------------------------------------------------------
// THE SAFETY PROPERTY — read this before changing an accessor
// -----------------------------------------------------------------------
// Every accessor here falls back to the guardrail data/metricRegistry.js
// export (imported read-only, never modified) when:
//   (a) the API call failed / is unreachable, or
//   (b) the fetch hasn't resolved yet (still loading), or
//   (c) the metric genuinely isn't in the registry.
// So the worst case — registry API down entirely — reproduces EXACTLY
// today's frontend behaviour (metricRegistry.js's own labelFor/isCountLike,
// palettes.js's defaultPaletteFor). This context is a strict superset:
// it can only ever add real metadata for metrics the guardrail catalogue
// didn't know about, never remove or change what the guardrail already
// provided for a metric it does know.
//
// `unit` and `category` have no equivalent in the guardrail file — it only
// ever carried label + the count-like/aggregation set (see its header
// comment). Their fallbacks come from data/metricCatalog.js's own small
// offline table (offlineCategoryFor) or are simply null (unitFor), which —
// for `unit` — matches today's behaviour: no frontend file centralised a
// metric_type -> canonical unit map before this track (VisualPreview.jsx
// and VisualItemCreator.jsx sourced `unit` from live query results, not
// from any catalogue file, and this context does not change that; see the
// call sites this track rewires for how `unit` is handled there).
// -----------------------------------------------------------------------

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  fetchMetricRegistry,
  fetchMetricAliases,
  buildCsvAliasIndex,
  categoryDisplayLabel,
  offlineCategoryFor,
} from '../data/metricCatalog'
import { labelFor as guardrailLabelFor, isCountLike } from '../data/metricRegistry'
import { defaultPaletteFor } from '../data/palettes'
import { setCsvAliasSource } from '../utils/csvPreviewParser'
import { useAuth } from './AuthContext'
import { planRegistryLoad } from './metricRegistryLoad.mjs'

const MetricRegistryContext = createContext(null)

export const useMetricRegistry = () => {
  const ctx = useContext(MetricRegistryContext)
  if (!ctx) {
    throw new Error('useMetricRegistry must be used within a MetricRegistryProvider')
  }
  return ctx
}

export const MetricRegistryProvider = ({ children }) => {
  const { isAuthenticated } = useAuth()

  // null = registry fetch hasn't resolved (success OR failure) yet. Every
  // accessor treats "not yet resolved" the same as "resolved but empty":
  // fall back. Once resolved (even to an empty Map, e.g. on API failure),
  // `loaded` becomes true and unregistered-metric lookups become permanent
  // (not "might still be loading") rather than transient.
  const [metricsByType, setMetricsByType] = useState(null)
  const [aliases, setAliases] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    // The registry endpoints require auth, and this provider wraps the whole
    // app — including the public landing page, where no visitor has a token.
    // Firing anyway meant every anonymous visit logged two 401s and a red
    // "metric registry API unreachable" error to the console of the Ministry's
    // public site, describing a failure that isn't one. With no session,
    // resolve straight to the empty/fallback state the catch branch would have
    // produced: identical behaviour, no bogus request, no bogus error.
    //
    // Keying the effect on `isAuthenticated` also closes a real gap. This
    // provider sits under AuthProvider and above the router, so it does NOT
    // remount on a client-side login. With `[]` deps, anyone who landed on
    // `/` first took the anonymous 401, cached the empty registry, and then
    // ran their whole authenticated session on bundled fallbacks — silently,
    // since the accessors degrade rather than throw. Now the fetch runs the
    // moment a session exists.
    const plan = planRegistryLoad({ isAuthenticated })

    if (!plan.fetch) {
      setMetricsByType(new Map())
      setLoading(plan.loading)
      return () => {
        cancelled = true
      }
    }

    // Re-entering the authenticated branch (login after an anonymous landing
    // visit) means the previous pass already flipped `loading` to false.
    setLoading(plan.loading)

    Promise.all([
      fetchMetricRegistry(),
      // Aliases are a secondary, non-critical fetch (disease_key catalogue
      // — not consumed by any of the consumers this track rewires). Don't
      // let its failure block the metrics fetch or the fallback contract.
      fetchMetricAliases().catch(() => []),
    ])
      .then(([metrics, aliasRows]) => {
        if (cancelled) return
        setMetricsByType(new Map(metrics.map((m) => [m.metricType, m])))
        setAliases(aliasRows)
        setError(null)
        // Hydrate csvPreviewParser's live alias index. Safe to call even if
        // metrics is empty — setCsvAliasSource treats an empty index as
        // "no live source", which keeps csvPreviewParser on its own
        // built-in offline table.
        setCsvAliasSource(buildCsvAliasIndex(metrics))
      })
      .catch((err) => {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.error(
          '[MetricRegistryContext] metric registry API unreachable — falling back to bundled defaults (data/metricRegistry.js, data/palettes.js) for every accessor:',
          err
        )
        setMetricsByType(new Map()) // loaded=true, empty => every lookup falls back
        setError(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  const loaded = metricsByType !== null

  const get = (metricType) => (loaded && metricType ? metricsByType.get(metricType) : undefined)

  const labelFor = (metricType) => {
    const m = get(metricType)
    return m?.label || guardrailLabelFor(metricType)
  }

  const shortLabelFor = (metricType) => {
    const m = get(metricType)
    return m?.shortLabel || labelFor(metricType)
  }

  const unitFor = (metricType) => {
    const m = get(metricType)
    return m?.unit ?? null
  }

  const unitLabelFor = (metricType) => {
    const m = get(metricType)
    return m?.unitLabel || m?.unit || null
  }

  // 'sum' for count-like metrics (incidence reports), 'mean' for indices /
  // continuous measurements — same policy ChartComponent.jsx's own fallback
  // encodes via isCountLike. The registry's `aggregation` column was seeded
  // by evaluating that exact logic (see infrastructure/migrations/0017's
  // commit message), so this is a no-op for every metric the guardrail
  // already classified — it only adds a value for metrics the guardrail
  // never saw.
  const aggregationFor = (metricType) => {
    const m = get(metricType)
    return m?.aggregation || (isCountLike(metricType) ? 'sum' : 'mean')
  }

  // Same reasoning as aggregationFor: the registry's `palette` column was
  // populated "by evaluating defaultPaletteFor() logic once at seed time,
  // NOT by importing the guardrail file" (IMPORT_SPEC.md §1.1) — so for a
  // metric the guardrail already had an opinion on, this returns the exact
  // same palette id.
  const paletteFor = (metricType) => {
    const m = get(metricType)
    return m?.palette || defaultPaletteFor(metricType)
  }

  // Raw registry category enum (disease | meteorological | air_pollutant |
  // vegetation_water). Falls back to metricCatalog's small offline table —
  // see this file's header for why that table exists instead of extending
  // the guardrail.
  const categoryFor = (metricType) => {
    const m = get(metricType)
    return m?.category || offlineCategoryFor(metricType)
  }

  // Human display string for categoryFor's enum, e.g. 'Health / Disease'.
  const categoryDisplayFor = (metricType) => categoryDisplayLabel(categoryFor(metricType))

  const decimalsFor = (metricType) => {
    const m = get(metricType)
    return Number.isFinite(m?.decimals) ? m.decimals : 1
  }

  // Only true once the registry has actually loaded AND confirmed this
  // metric_type has no registry row (but does have data — see
  // metricCatalog.js normalizeMetric). Deliberately NOT true while still
  // loading — "don't know yet" is not the same claim as "confirmed
  // unregistered".
  const isUnregistered = (metricType) => {
    const m = get(metricType)
    return m ? !!m.unregistered : false
  }

  const value = useMemo(
    () => ({
      loading,
      loaded,
      error,
      metrics: loaded ? Array.from(metricsByType.values()) : [],
      aliases,
      labelFor,
      shortLabelFor,
      unitFor,
      unitLabelFor,
      aggregationFor,
      paletteFor,
      categoryFor,
      categoryDisplayFor,
      decimalsFor,
      isUnregistered,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, loaded, error, metricsByType, aliases]
  )

  return <MetricRegistryContext.Provider value={value}>{children}</MetricRegistryContext.Provider>
}

export default MetricRegistryContext
